import { useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL || "";

const SITE_LABELS = {
  ashburn: "Ashburn, VA", chicago: "Chicago, IL",
  dallas: "Dallas, TX", frederick: "Frederick, MD",
  phoenix: "Phoenix, AZ", portland: "Portland, OR",
  saltlake: "Salt Lake City, UT", bogota: "Bogota",
  buenosaires: "Buenos Aires", lima: "Lima",
  mexicocity: "Mexico City", santiago: "Santiago",
  saopaulo: "Sao Paulo",
};

const fmtDate = (iso) => {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const Section = ({ title, label, color, count, defaultOpen, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const accents = { amber: "#F59E0B", blue: "#185FA5", teal: "#14B8A6" };
  const accent = accents[color] || "#5f5e6e";
  return (
    <div style={{ borderLeft: `3px solid ${accent}`, background: "#12121f",
                  borderRadius: "0 8px 8px 0", overflow: "hidden", marginBottom: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center",
          justifyContent: "space-between", padding: "12px 16px",
          background: "transparent", border: "none", cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            background: accent, color: "#000", fontSize: 10,
            fontWeight: 700, padding: "2px 6px", borderRadius: 3,
            letterSpacing: "0.05em",
          }}>{label}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#e8e6de" }}>{title}</span>
          <span style={{ fontSize: 11, color: "#5f5e6e", fontWeight: 400 }}>
            {count} {count === 1 ? "file" : "files"}
          </span>
        </span>
        <span style={{ color: "#5f5e6e", fontSize: 11 }}>{open ? "\u25B2" : "\u25BC"}</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
};

const EmptyState = ({ message }) => (
  <p style={{ fontSize: 11, color: "#5f5e6e", fontStyle: "italic", padding: "8px 16px" }}>
    {message}
  </p>
);

const BriefingRow = ({ b }) => {
  const colors = {
    portfolio: { color: "#185FA5", bg: "#185FA522", label: "Portfolio" },
    site:      { color: "#1D9E75", bg: "#1D9E7522", label: "Site" },
    ferc:      { color: "#F59E0B", bg: "#F59E0B22", label: "FERC Intel" },
  };
  const meta = colors[b.type] || colors.portfolio;

  // Parse display title
  let title = "Portfolio Morning Briefing";
  if (b.type === "ferc") {
    title = "FERC Intelligence Brief";
  } else if (b.type === "site") {
    const match = b.filename.match(/axiom_site_([a-z]+)_/i);
    const key = match ? match[1].toLowerCase() : null;
    title = key && SITE_LABELS[key]
      ? `Site Intelligence \u2014 ${SITE_LABELS[key]}`
      : "Site Briefing";
  }

  return (
    <a
      href={`${API}${b.download_url}`}
      target="_blank"
      rel="noreferrer"
      style={{ textDecoration: "none", display: "block" }}
    >
      <div
        style={{
          background: "#151525", padding: "10px 16px",
          display: "flex", justifyContent: "space-between",
          alignItems: "center", cursor: "pointer",
          borderBottom: "1px solid #1e1e30",
          transition: "background 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = "#1a1a2e"}
        onMouseLeave={e => e.currentTarget.style.background = "#151525"}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 6,
            background: meta.bg, display: "flex", alignItems: "center",
            justifyContent: "center", flexShrink: 0,
            fontSize: 11, color: meta.color, fontWeight: 700,
          }}>PDF</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#e8e6de", marginBottom: 1 }}>
              {title}
            </div>
            <div style={{ fontSize: 10, color: "#5f5e6e" }}>{b.filename}</div>
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <span style={{
            fontSize: 9, color: meta.color, background: meta.bg,
            padding: "2px 8px", borderRadius: 10,
          }}>{meta.label}</span>
          <div style={{ fontSize: 10, color: "#5f5e6e", marginTop: 4 }}>
            {fmtDate(b.generated_at)} &middot; {b.size_kb} KB
          </div>
        </div>
      </div>
    </a>
  );
};

export default function Briefings() {
  const [briefings, setBriefings] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generateStatus, setGenerateStatus] = useState(null);

  const API_KEY = process.env.REACT_APP_API_KEY || "";

  const load = () => {
    setLoading(true);
    fetch(`${API}/admin/briefing/list`)
      .then(r => r.json())
      .then(d => { setBriefings(d.briefings || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const generateAll = async () => {
    setGenerating(true);
    setGenerateStatus("Generating portfolio briefing...");
    try {
      const r1 = await fetch(`${API}/admin/briefing/generate`, {
        headers: { "X-API-Key": API_KEY },
      });
      if (!r1.ok) throw new Error("Portfolio briefing failed");

      setGenerateStatus("Generating site briefings (this takes ~60 seconds)...");
      const r2 = await fetch(`${API}/admin/briefing/site/all`, {
        method: "POST",
        headers: { "X-API-Key": API_KEY },
      });
      if (!r2.ok) throw new Error("Site briefings failed");

      setGenerateStatus("Generating FERC briefing...");
      try {
        await fetch(`${API}/admin/ferc/briefing/generate`, {
          headers: { "X-API-Key": API_KEY },
        });
      } catch (fercErr) {
        console.error("FERC briefing failed (non-blocking):", fercErr);
      }

      setGenerateStatus("Complete. Refreshing...");
      setTimeout(() => { load(); setGenerateStatus(null); }, 3000);
    } catch (e) {
      setGenerateStatus(`Error: ${e.message}`);
      setTimeout(() => setGenerateStatus(null), 5000);
    } finally {
      setGenerating(false);
    }
  };

  // Group briefings
  const grouped = {
    ferc: briefings.filter(b => b.type === "ferc"),
    portfolio: briefings.filter(b => b.type === "portfolio"),
    site: {},
  };
  briefings.filter(b => b.type === "site").forEach(b => {
    const match = b.filename.match(/axiom_site_([a-z]+)_/i);
    const key = match ? match[1].toLowerCase() : "other";
    if (!grouped.site[key]) grouped.site[key] = [];
    grouped.site[key].push(b);
  });

  const portfolioCount = grouped.portfolio.length;
  const siteCount = briefings.filter(b => b.type === "site").length;
  const fercCount = grouped.ferc.length;

  if (loading) return (
    <div style={{ padding: 48, color: "#9a99a2" }}>Loading briefings...</div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#e2e0d8",
                  fontFamily: "'Inter', sans-serif", padding: "24px 32px" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-end", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 11, color: "#5f5e6e", letterSpacing: 2,
                        textTransform: "uppercase", marginBottom: 4 }}>
            Axiom Intelligence
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "#e8e6de" }}>
            Briefings
          </h1>
          <div style={{ fontSize: 12, color: "#5f5e6e", marginTop: 4 }}>
            {fercCount} FERC &middot; {portfolioCount} portfolio &middot; {siteCount} site briefings &middot;
            auto-generated daily
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <button
            onClick={generateAll}
            disabled={generating}
            style={{
              background: generating ? "#2a2a3e" : "#185FA5",
              color: generating ? "#5f5e6e" : "#fff",
              padding: "8px 20px", borderRadius: 6, fontSize: 12,
              border: "none", cursor: generating ? "not-allowed" : "pointer",
              fontWeight: 500,
            }}
          >
            {generating ? "Generating..." : "Generate All Now"}
          </button>
          {generateStatus && (
            <div style={{ fontSize: 11, color: "#BA7517" }}>
              {generateStatus}
            </div>
          )}
        </div>
      </div>

      {/* Grouped sections */}
      <div>

        {/* FERC Intelligence */}
        <Section title="FERC Intelligence" label="FERC" color="amber"
                 count={fercCount} defaultOpen={true}>
          {fercCount === 0
            ? <EmptyState message="No FERC briefings generated yet." />
            : grouped.ferc.slice(0, 10).map(b => <BriefingRow key={b.filename} b={b} />)
          }
        </Section>

        {/* Portfolio */}
        <Section title="Portfolio Morning Brief" label="PORTFOLIO" color="blue"
                 count={portfolioCount} defaultOpen={true}>
          {portfolioCount === 0
            ? <EmptyState message="No portfolio briefings generated yet." />
            : grouped.portfolio.slice(0, 5).map(b => <BriefingRow key={b.filename} b={b} />)
          }
          {portfolioCount > 5 && (
            <p style={{ fontSize: 10, color: "#5f5e6e", padding: "6px 16px" }}>
              + {portfolioCount - 5} older briefings
            </p>
          )}
        </Section>

        {/* Site Intelligence */}
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#5f5e6e",
                        textTransform: "uppercase", letterSpacing: 2, marginBottom: 8 }}>
            Site Intelligence
          </div>
          {Object.entries(grouped.site).length === 0 ? (
            <EmptyState message="No site briefings generated yet." />
          ) : (
            Object.entries(grouped.site)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([key, rows]) => (
                <Section key={key}
                  title={SITE_LABELS[key] || key}
                  label="SITE"
                  color="teal"
                  count={rows.length}
                  defaultOpen={false}
                >
                  {rows.slice(0, 10).map(b => <BriefingRow key={b.filename} b={b} />)}
                  {rows.length > 10 && (
                    <p style={{ fontSize: 10, color: "#5f5e6e", padding: "6px 16px" }}>
                      + {rows.length - 10} older briefings
                    </p>
                  )}
                </Section>
              ))
          )}
        </div>

      </div>
    </div>
  );
}
